import {
  QueryErrorResetBoundary,
  QueryKey,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import ContentLoading from '@components/content-loading';
import InternalErrorBoundary from '@components/internal-error-boundary';
import SuspenseQueryContent from '@components/suspense-query-content';
import { captureFrontendException } from '@lib/observability/sentry';

interface BoundaryProps {
  loadingFallback?: ReactNode;
  errorFallback?: (onRetry: () => void) => ReactNode;
}

interface ContentBoundaryProps extends BoundaryProps {
  children: ReactNode;
  options?: never;
}

interface QueryBoundaryProps<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> extends BoundaryProps {
  children: (queryResult: UseSuspenseQueryResult<TData, TError>) => ReactNode;
  options: UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>;
}

const fallback = <ContentLoading />;

const DataBoundary = <TQueryFnData, TError, TData, TQueryKey extends QueryKey>({
  children,
  errorFallback,
  loadingFallback = fallback,
  options,
}:
  | ContentBoundaryProps
  | QueryBoundaryProps<TQueryFnData, TError, TData, TQueryKey>) => {
  const content = options ? (
    <SuspenseQueryContent options={options}>{children}</SuspenseQueryContent>
  ) : (
    children
  );

  return (
    <QueryErrorResetBoundary>
      {({ reset }) =>
        errorFallback ? (
          <ErrorBoundary
            fallbackRender={({ resetErrorBoundary }) =>
              errorFallback(resetErrorBoundary)
            }
            onError={(error, info) => {
              captureFrontendException(error, {
                componentStack: info.componentStack,
              });
            }}
            onReset={reset}
          >
            <Suspense fallback={loadingFallback}>{content}</Suspense>
          </ErrorBoundary>
        ) : (
          <ErrorBoundary
            FallbackComponent={InternalErrorBoundary}
            onError={(error, info) => {
              captureFrontendException(error, {
                componentStack: info.componentStack,
              });
            }}
            onReset={reset}
          >
            <Suspense fallback={loadingFallback}>{content}</Suspense>
          </ErrorBoundary>
        )
      }
    </QueryErrorResetBoundary>
  );
};

export default DataBoundary;
